/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import DS from 'ember-data';
import { dropTask } from 'ember-concurrency';
import SqlImportModel from 'cio/models/sql-import';
import SqlImportDatabaseModel from 'cio/models/sql-import-database';
import FormValidations from 'cio/utils/form-validations';
import { SqlImportDatabase } from 'cio/utils/sql-import';
import ApiErrorsService from 'cio/services/api-errors';
import AnalyticsService from 'cio/services/analytics';
import AccountService from 'cio/services/account';
import { DataCenter } from 'cio/models/account';
import { SQL_SERVER_IPS } from 'cio/utils/data-centers';
import { assertExists } from 'cio/utils/assertions';
import { isEmpty } from '@ember/utils';
import isDestroyed from 'cio/utils/is-destroyed';

interface SqlImportsCreateDatabaseArgs {
  type: SqlImportDatabase;
  model: SqlImportModel;
  databases: SqlImportDatabaseModel[];
  onCreateDatabase: (database: SqlImportDatabaseModel) => unknown;
  onDeleteDatabase: (database: SqlImportDatabaseModel) => unknown;
  onSubmit: () => unknown;
  onCancel: () => unknown;
  readOnly: boolean;
}

const BigQueryValidations = {
  projectId(value?: string) {
    if (!value || value?.trim() === '') {
      return "can't be blank";
    }

    return true;
  },
};

const Validations = {
  name(value?: string) {
    if (!value || value?.trim() === '') {
      return "can't be blank";
    }

    return true;
  },

  username(value?: string) {
    if (!value || value?.trim() === '') {
      return "can't be blank";
    }

    return true;
  },

  host(value?: string) {
    if (!value || value?.trim() === '') {
      return "can't be blank";
    }
    return true;
  },

  port(this: SqlImportsCreateDatabase, value?: string) {
    if (this.args.type === SqlImportDatabase.snowflake) {
      return true;
    }

    if (!value || String(value)?.trim() === '') {
      return "can't be blank";
    }

    let int = parseInt(value);
    if (isNaN(int) || int <= 0) {
      return 'must be a positive number';
    }

    return true;
  },
};

enum EditModes {
  edit = 'edit',
  add = 'add',
}

export default class SqlImportsCreateDatabase extends Component<SqlImportsCreateDatabaseArgs> {
  @service store!: DS.Store;
  @service apiErrors!: ApiErrorsService;
  @service analytics!: AnalyticsService;
  @service account!: AccountService;

  readonly validations: FormValidations;
  readonly databaseTypes = SqlImportDatabase;

  @tracked database: SqlImportDatabaseModel | null = null;
  @tracked databaseToDelete: SqlImportDatabaseModel | null = null;
  @tracked databases = this.args.databases.toArray();
  @tracked editMode: EditModes | null = null;
  @tracked hasSelectedValidatedDb = false;

  readonly editModes = EditModes;

  get sqlServerIPAddress() {
    return SQL_SERVER_IPS[this.account?.current?.dataCenter ?? DataCenter.US];
  }

  get isSslCompliant() {
    if (this.database?.ssl === true && this.database.type === this.databaseTypes.mysql) {
      // if one of the fields has a value, we want all 3 to have values. it's all or nothing
      let fields = [this.database.sslClientCert, this.database.sslClientKey, this.database.sslServerCA];
      if (fields.some((field) => !isEmpty(field))) {
        return fields.filter((field) => !isEmpty(field)).length === fields.length;
      }
    }
    return true;
  }

  constructor(owner: unknown, args: SqlImportsCreateDatabaseArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      context: this,
      validations: this.pickValidation(),
      modelKey: 'database',
    });

    if (this.args.databases.length === 0) {
      this.addDatabase();
    }

    if (this.args.databases.length === 1) {
      this.autoSelectSingleDatabase();
    }

    if (this.args.databases.length > 1) {
      this.autoSelectInUseDatabase();
    }
  }

  private pickValidation() {
    switch (this.args.type) {
      case SqlImportDatabase.bigQuery:
        return BigQueryValidations;
      case SqlImportDatabase.snowflake:
        return {
          ...Validations,
          host(value?: string) {
            if (!value || value?.trim() === '') {
              return "can't be blank";
            }

            if (value.charAt(0) === '.' || value.charAt(value.length - 1) === '.') {
              return 'cannot start or end with a period';
            }
            return true;
          },
        };
      default:
        return Validations;
    }
  }

  private autoSelectSingleDatabase() {
    // If there's only one database, autoselect it
    this.database = this.args.databases[0]!;
    this.setDefaultEditMode();
  }

  private autoSelectInUseDatabase() {
    this.args.databases.map((db) => {
      if (db.inUse) {
        this.hasSelectedValidatedDb = true;
      }
    });
  }

  private async setDefaultEditMode() {
    assertExists(this.database);

    // if selected database passes validation they are not in edit mode.
    // else, force user to make changes then validate later.
    try {
      await this.validations.validateModel();
      this.hasSelectedValidatedDb = true;
      this.args.model.set('database', this.database);
    } catch {
      this.validations.clearErrors();
      this.hasSelectedValidatedDb = false;
      this.editMode = EditModes.edit;
      return;
    }
  }

  get usesSSL() {
    return (
      this.args.type === SqlImportDatabase.mysql ||
      this.args.type === SqlImportDatabase.postgres ||
      this.args.type === SqlImportDatabase.redshift
    );
  }

  get usesPort() {
    return (
      this.args.type === SqlImportDatabase.mysql ||
      this.args.type === SqlImportDatabase.postgres ||
      this.args.type === SqlImportDatabase.redshift ||
      this.args.type === SqlImportDatabase.sqlserver
    );
  }

  willDestroy() {
    super.willDestroy();

    if (this.database != null && !isDestroyed(this.database)) {
      this.database.rollbackAttributes();
    }
  }

  @action
  async chooseDatabase(db: SqlImportDatabaseModel) {
    this.args.model.set('database', db);
    this.database = db;
    try {
      await this.validations.validateModel();
      this.hasSelectedValidatedDb = true;
    } catch {
      this.hasSelectedValidatedDb = false;
    }
  }

  @action
  addDatabase() {
    this.database = this.store.createRecord('sql-import-database', { type: this.args.type });
    this.editMode = EditModes.add;
    this.analytics.track('sql_imports', 'add_database_started', { model: this.args.model });
  }

  @action
  editDatabase(db: SqlImportDatabaseModel) {
    this.analytics.track('sql_imports', 'edit_database_started', { model: this.args.model });
    this.database = db;
    this.editMode = EditModes.edit;
  }

  @action
  cancelEditingDatabase() {
    this.database?.rollbackAttributes();
    this.editMode = null;
    this.hasSelectedValidatedDb = false;
    this.validations.clearErrors();
  }

  saveDatabaseTask = dropTask(async (ev: MouseEvent | KeyboardEvent) => {
    ev.preventDefault();
    assertExists(this.database);

    let isNew = this.database.get('isNew') ?? false;

    try {
      await this.validations.validateModel();
    } catch {
      return;
    }

    try {
      await this.database.save();

      this.args.model.set('database', this.database);
      if (isNew) {
        this.args.onCreateDatabase(this.database);
      }

      this.editMode = null;
      if (this.args.databases.length === 1) {
        this.autoSelectSingleDatabase();
      }
    } catch (res) {
      this.analytics.track('sql_imports', isNew ? 'add_database_failed' : 'edit_database_failed', {
        errorMessage: res?.errors?.[0].detail,
      });

      if (res?.errors?.[0].status === '422' || res?.errors?.[0].source != null) {
        return;
      }
      this.apiErrors.report(res);
    }
  });

  deleteDatabaseTask = dropTask(async () => {
    assertExists(this.databaseToDelete);

    this.analytics.track('sql_imports', 'delete_database_started', {
      model: this.args.model,
    });

    try {
      this.databaseToDelete.destroyRecord();
      this.args.onDeleteDatabase(this.databaseToDelete);
      this.databaseToDelete = null;

      if (this.args.databases.length === 0) {
        this.addDatabase();
      }
    } catch (res) {
      this.apiErrors.report(res);
    }
  });
}
